import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { resetStore } from "app/actions";
import { Container } from "interfaces/Containers";
import { Device } from "interfaces/Device";
import { Player } from "interfaces/Players";

import { RootState } from "../../app/store";
import { DEVICES_TYPES } from "./devicesSlice";

export enum MODAL_TYPES {
  ADD = "add",
  EDIT = "edit",
  DELETE = "delete",
  CONFIRMATION = "confirm",
  CANCEL = "cancel",
}

export enum MODAL_SECTIONS {
  PLAYERS = "players",
  CONTAINERS = "containers",
  DEVICES = "devices",
  SESSION = "session",
}

export enum SESSION_MODAL_TYPES {
  CANCEL = "cancelSession",
  FINALIZE = "finalizeSession",
  LOW_BATTERY = "lowBattery",
}

export interface State {
  isOpen: boolean;
  player?: Player;
  container?: Container;
  type?: MODAL_TYPES;
  section?: MODAL_SECTIONS;
  containerId?: string;
  device?: Device;
  deviceType?: DEVICES_TYPES;
  hideLayout?: boolean;
  matchId?: string;
  sessionId?: string;
  extraData?: {
    sessionModalType: SESSION_MODAL_TYPES;
  };
}

const initialState: State = {
  isOpen: false,
  type: MODAL_TYPES.ADD,
  player: undefined,
  container: undefined,
  section: undefined,
  containerId: undefined,
  device: undefined,
  deviceType: DEVICES_TYPES.HEADSET,
  hideLayout: false,
  matchId: undefined,
  sessionId: undefined,
  extraData: undefined,
};

type OpenModalRequest = {
  type: MODAL_TYPES;
  selected?: Player | Container | Device;
  section?: MODAL_SECTIONS;
  containerId?: string;
  deviceType?: DEVICES_TYPES;
  matchId?: string;
  sessionId?: string;
  extraData?: {
    sessionModalType: SESSION_MODAL_TYPES;
  };
};

export const modalSlice = createSlice({
  name: "modal",
  initialState,
  reducers: {
    openModal: (state, action: PayloadAction<OpenModalRequest>) => {
      state.isOpen = true;
      state.type = action.payload.type;

      if (action.payload.section) {
        state.section = action.payload.section;

        switch (action.payload.section) {
          case MODAL_SECTIONS.PLAYERS:
            state.player = action.payload.selected as Player;
            break;
          case MODAL_SECTIONS.CONTAINERS:
            state.container = action.payload.selected as Container;
            break;
          case MODAL_SECTIONS.DEVICES:
            state.device = action.payload.selected as Device;
            break;
        }
      }

      if (action.payload.containerId) {
        state.containerId = action.payload.containerId;
      }

      if (action.payload.matchId) {
        state.matchId = action.payload.matchId;
      }

      if (action.payload.sessionId) {
        state.sessionId = action.payload.sessionId;
      }

      if (action.payload.deviceType) {
        state.deviceType = action.payload.deviceType;
      }
      state.extraData = action.payload.extraData;
    },
    closeModal: (state) => {
      state.isOpen = false;
      state.matchId = undefined;
      state.sessionId = undefined;
    },
    resetFlags: (state) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      state = initialState;
    },
    showLayout: (state) => {
      state.hideLayout = false;
    },
    hideLayout: (state) => {
      state.hideLayout = true;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetStore, () => initialState);
  },
});

export const { openModal, closeModal, resetFlags, showLayout, hideLayout } =
  modalSlice.actions;

// ------ Selectors ------
export const getModalState = (state: RootState) => {
  const common: State & { selected?: Container | Device } = {
    isOpen: state.modal.isOpen,
    type: state.modal.type,
    containerId: state.modal.containerId,
    deviceType: state.modal.deviceType,
    section: state.modal.section,
    matchId: state.modal.matchId,
    sessionId: state.modal.sessionId,
    extraData: state.modal.extraData,
  };

  switch (state.modal.section) {
    case MODAL_SECTIONS.CONTAINERS:
      common.selected = state.modal.container as Container;
      break;
    case MODAL_SECTIONS.DEVICES:
      common.selected = state.modal.device as Device;
      break;
  }

  return common;
};

export const getLayoutOption = (state: RootState) => ({
  hideLayout: state.modal.hideLayout,
});

export default modalSlice.reducer;
